/**
 * Backup codes, shown exactly once.
 *
 * What a code does is stated plainly because it is narrow: with your
 * password, on the sign-in page, one code lets you register a new phone. It
 * does not sign you in by itself, and each works once. The server keeps only
 * hashes, so nobody — including an administrator — can show these again.
 */
import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';

type Props = {
  codes: string[];
  /** Called when the person confirms they have stored the codes. */
  onSaved?: () => void;
  /** Extra sentence, e.g. that earlier codes stopped working. */
  note?: string;
};

export default function BackupCodesPanel({ codes, onSaved, note }: Props) {
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(codes.join('\n'));
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section
      aria-labelledby="backup-codes-title"
      className="rounded-md border border-[#e8dcc0] bg-[#fdf4e6] p-4 text-[#10252d]"
    >
      <h2 id="backup-codes-title" className="text-sm font-semibold">Save your backup codes now</h2>
      <p className="mt-1 text-xs leading-5 text-[#7a6540]">
        If you lose your phone, enter your password on the sign-in page, choose
        &ldquo;Use a backup code&rdquo;, and enter one of these to register a new phone.
        Each code works once. They are shown only now — nobody can look them up later.
      </p>
      {note && <p className="mt-1 text-xs leading-5 text-[#7a6540]">{note}</p>}
      <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 font-mono text-sm tracking-wider" aria-label="Backup codes">
        {codes.map((c) => <li key={c}>{c}</li>)}
      </ul>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={copy}
          className="inline-flex h-8 items-center gap-1.5 rounded border border-[#d7e5e1] bg-white px-3 text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-[#17685d]"
        >
          {copied ? <Check className="h-3 w-3" aria-hidden /> : <Copy className="h-3 w-3" aria-hidden />}
          {copied ? 'Copied' : 'Copy all'}
        </button>
        {onSaved && (
          <label className="inline-flex items-center gap-2 text-xs font-medium">
            <input
              type="checkbox"
              checked={saved}
              onChange={(e) => { setSaved(e.target.checked); if (e.target.checked) onSaved(); }}
            />
            I have stored these somewhere safe
          </label>
        )}
      </div>
    </section>
  );
}
